import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Settings as SettingsIcon, X, User, Database, Cpu, Users as UsersIcon, LogOut } from 'lucide-react';
import { useUser } from '../../context/UserContext';
import { ReactComponent as UpstoxMark } from '../../assets/upstox-mark.svg';

// ── Inline SVG icon paths (24-grid, stroke-width 1.75, currentColor) ─────────
const ICONS = {
  dashboard:       '<rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/><rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/>',
  nifty:           '<path d="M3 13h3l2.5-6 4 13 3-9 1.8 4H21"/>',
  market:          '<path d="M5 20v-9"/><path d="M12 20V4"/><path d="M19 20v-6"/><path d="M3 20h18"/>',
  options:         '<path d="M12 3 3 7.5l9 4.5 9-4.5z"/><path d="M3 12l9 4.5 9-4.5"/><path d="M3 16.5l9 4.5 9-4.5"/>',
  strategies:      '<circle cx="12" cy="12" r="8.5"/><circle cx="12" cy="12" r="4"/><circle cx="12" cy="12" r="0.6" fill="currentColor"/>',
  journal:         '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5z"/><path d="M9 7h7"/>',
  news:            '<path d="M4 5h13v15H6a2 2 0 0 1-2-2z"/><path d="M17 9h3v9a2 2 0 0 1-2 2"/><path d="M7 9h7M7 13h7M7 17h5"/>',
  perfTables:      '<rect x="3.5" y="4.5" width="17" height="15" rx="1.5"/><path d="M3.5 9.5h17M3.5 14.5h17M9 4.5v15"/>',
  relStrength:     '<path d="M3 17l5-4 5 3 8-9"/><path d="M3 8l5 4 5-3 8 9"/>',
  sectorRotation:  '<path d="M12 3v18M3 12h18"/><circle cx="7.5" cy="7.5" r="1.3" fill="currentColor"/><circle cx="16.5" cy="8.5" r="1.3" fill="currentColor"/><circle cx="7.5" cy="16" r="1.3" fill="currentColor"/><circle cx="16" cy="15.5" r="1.3" fill="currentColor"/>',
  indexDrivers:    '<path d="M4 18a9 9 0 1 1 16 0"/><path d="M12 18l4-5"/><circle cx="12" cy="18" r="1.2" fill="currentColor"/>',
  eventsActions:   '<rect x="4" y="5" width="16" height="16" rx="2"/><path d="M4 9.5h16M8 3v4M16 3v4"/><path d="M12 12.5a2 2 0 0 0-2 2c0 1.3-.6 1.7-.9 2h5.8c-.3-.3-.9-.7-.9-2a2 2 0 0 0-2-2z"/>',
  nseDirectory:    '<path d="M5 4h14v16H5z"/><path d="M5 9h14"/><path d="M8 13h5M8 16h3"/>',
  marketAnalyst:   '<path d="M12 4l1.6 4.4L18 10l-4.4 1.6L12 16l-1.6-4.4L6 10l4.4-1.6z"/><circle cx="19" cy="5" r="2.2"/><path d="M19 3v1.5M19 6.5V8M17.3 4.3l1 1M20.7 5.7l1 1M16.8 5h1.5M20.7 5h1.5M17.3 5.7l1-1M20.7 4.3l1-1"/>',
  optionChain:     '<rect x="3" y="8.5" width="11" height="7" rx="3.5"/><rect x="10" y="8.5" width="11" height="7" rx="3.5"/>',
  oiAnalysis:      '<path d="M3 20h18"/><rect x="5" y="11" width="2.6" height="6" rx="0.5"/><rect x="10.7" y="8" width="2.6" height="9" rx="0.5"/><rect x="16.4" y="13" width="2.6" height="4" rx="0.5"/><path d="M5 9l5.5-3 5.5 2 3-3"/>',
  strategyBuilder: '<rect x="4" y="4" width="7" height="7" rx="1"/><rect x="13" y="4" width="7" height="7" rx="1"/><rect x="4" y="13" width="7" height="7" rx="1"/><path d="M16.5 13.5v6M13.5 16.5h6"/>',
  stratList:       '<circle cx="12" cy="12" r="8.5"/><circle cx="12" cy="12" r="4"/><circle cx="12" cy="12" r="0.6" fill="currentColor"/>',
  watchlist:       '<path d="M4 5h9M4 10h9M4 15h5"/><path d="M17 4h4v10l-2-1.6L17 14z"/>',
  observations:    '<path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z"/><circle cx="12" cy="12" r="2.8"/>',
  paperTrading:    '<path d="M6 2h8l4 4v16H6z"/><path d="M14 2v4h4"/><path d="M9 13l2 2 4-4"/>',
  liveTrading:     '<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>',
  aiSummary:       '<path d="M12 4l1.6 4.4L18 10l-4.4 1.6L12 16l-1.6-4.4L6 10l4.4-1.6z"/><path d="M18 15l.7 1.8L20.5 17.5l-1.8.7L18 20l-.7-1.8L15.5 17.5l1.8-.7z"/>',
  openPositions:   '<path d="M8 13V5M5 8l3-3 3 3"/><path d="M16 11v8M13 16l3 3 3-3"/>',
  tradeJournal:    '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5z"/><path d="M9 7h7"/>',
  analytics:       '<path d="M4 4v16h16"/><path d="M7 14l3-4 3 2 4-6"/><circle cx="7" cy="14" r="1" fill="currentColor"/><circle cx="13" cy="12" r="1" fill="currentColor"/><circle cx="17" cy="6" r="1" fill="currentColor"/>',
  realizedPnl:     '<path d="M12 2v20M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>',
  desk:            '<rect x="3" y="4" width="18" height="12" rx="1.5"/><path d="M8 20h8M12 16v4"/>',
};

function NavIcon({ name, size = 18 }) {
  // upstox-041: the Upstox mark is a filled vector (the trader's own logo),
  // drawn in currentColor so it follows the sidebar's tone like the others
  if (name === 'upstox') return <UpstoxMark width={size} height={size} aria-hidden="true" />;
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
      width={size} height={size}
      dangerouslySetInnerHTML={{ __html: ICONS[name] || '' }}
    />
  );
}

// Caret for expandable sections
function Caret({ open }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      width={13} height={13}
      style={{ transition: 'transform .18s', transform: open ? 'rotate(90deg)' : 'none' }}>
      <path d="m9 6 6 6-6 6"/>
    </svg>
  );
}

// ── Nav tree: spec keys + app routing keys ────────────────────────────────────
const NAV_TREE = [
  { key: 'dashboard',  appTab: 'dashboard',   label: 'Dashboard',     icon: 'dashboard'  },
  { key: 'nifty',      appTab: 'nifty',        label: 'NIFTY',         icon: 'nifty'      },
  { key: 'market',     appTab: 'performance',  label: 'Market',        icon: 'market',    subs: [
    { label: 'Performance Tables', appSub: 'performance', icon: 'perfTables',     permKey: 'market_perf'      },
    { label: 'Relative Strength',  appSub: 'rs',          icon: 'relStrength',    permKey: 'market_rs'        },
    { label: 'Sector Rotation',    appSub: 'rotation',    icon: 'sectorRotation', permKey: 'market_rotation'  },
    { label: 'Index Drivers',      appSub: 'drivers',     icon: 'indexDrivers',   permKey: 'market_drivers'   },
    { label: 'Events & Actions',   appSub: 'events',      icon: 'eventsActions',  permKey: 'market_events'    },
    { label: 'NSE Directory',      appSub: 'directory',   icon: 'nseDirectory',   permKey: 'market_directory' },
    { label: 'Market AI',          appSub: 'analyst',     icon: 'marketAnalyst',  permKey: 'market_perf'      },
  ]},
  { key: 'options',    appTab: 'options',      label: 'Options',       icon: 'options',   subs: [
    { label: 'Option Chain',     appSub: 'chain',   icon: 'optionChain',     permKey: 'options' },
    { label: 'OI Analysis',      appSub: 'oi',      icon: 'oiAnalysis',      permKey: 'options' },
    { label: 'Strategy Builder', appSub: 'builder', icon: 'strategyBuilder', permKey: 'options' },
  ]},
  { key: 'strategies', appTab: 'scanner',      label: 'Strategies',    icon: 'strategies', subs: [
    { label: 'Strategies', appSub: 'strategies', icon: 'stratList',  permKey: 'scanner' },
    { label: 'Watchlist',  appSub: 'watchlists', icon: 'watchlist',  permKey: 'scanner' },
    { label: 'AI Summary', appSub: 'ai',          icon: 'aiSummary', permKey: 'scanner' },
  ]},
  { key: 'journal',    appTab: 'journal',      label: 'Journal',       icon: 'journal',   subs: [
    { label: 'Open Positions', appSub: 'open',      icon: 'openPositions', permKey: 'journal' },
    { label: 'Trade Journal',  appSub: 'journal',   icon: 'tradeJournal',  permKey: 'journal' },
    { label: 'Realized P&L',  appSub: 'realized',  icon: 'realizedPnl',   permKey: 'journal' },
    { label: 'Analytics',      appSub: 'analytics', icon: 'analytics',     permKey: 'journal' },
    { label: 'AI Summary',     appSub: 'ai',        icon: 'aiSummary',     permKey: 'journal' },
  ]},
  { key: 'desk',       appTab: 'desk',          label: 'Desk',          icon: 'desk',      subs: [
    { label: 'Observations',  appSub: 'observations', icon: 'observations', permKey: 'desk' },
    { label: 'Paper Trading', appSub: 'paper',        icon: 'paperTrading', permKey: 'desk' },
    { label: 'Live Trading',  appSub: 'live',         icon: 'liveTrading',  permKey: 'desk' },
    { label: 'Positions',     appSub: 'upstox',       icon: 'upstox',       permKey: 'desk' },
  ]},
  { key: 'news',       appTab: 'ai',           label: 'Digest & News', icon: 'news'       },
];

const SETTINGS_SUBS = [
  { key: 'account', label: 'Account', Icon: User     },
  { key: 'data',    label: 'Data',    Icon: Database  },
  { key: 'system',  label: 'System',  Icon: Cpu       },
  { key: 'users',   label: 'Users',   Icon: UsersIcon },
];

// ── User menu — inline bottom-to-top expansion (expanded) or flyout (collapsed) ─
function SidebarUserMenu({ collapsed, onCloseMobile, navigate, tab, sub }) {
  const { user, logout } = useUser();
  const [menuOpen,     setMenuOpen]     = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const flyoutRef = useRef(null);

  const isSettingsActive = tab === 'settings';

  // Close flyout (collapsed sidebar only) on outside click
  useEffect(() => {
    if (!collapsed) return;
    const handler = e => {
      if (flyoutRef.current && !flyoutRef.current.contains(e.target)) {
        setMenuOpen(false);
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [collapsed]);

  if (!user) return null;

  const firstName = user.name?.split(' ')[0] ?? user.name ?? '';

  const handleAvatarClick = () => {
    if (menuOpen) { setMenuOpen(false); setSettingsOpen(false); }
    else          { setMenuOpen(true); }
  };

  const handleSettingsSub = (key) => {
    navigate('settings', key);
    onCloseMobile?.();
  };

  const handleLogout = () => {
    setMenuOpen(false);
    setSettingsOpen(false);
    logout();
  };

  const avatar = user.picture
    ? <img src={user.picture} alt={user.name}
        style={{ width: 26, height: 26, borderRadius: '50%', display: 'block', flexShrink: 0 }}/>
    : <div style={{
        width: 26, height: 26, borderRadius: '50%',
        background: 'var(--ink)', color: 'var(--paper)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700, flexShrink: 0,
      }}>
        {user.name?.[0]?.toUpperCase() || '?'}
      </div>;

  // Shared item button base (no background, no border)
  const itemBase = {
    width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
    fontFamily: 'var(--font-ui)', display: 'flex', alignItems: 'center',
    borderRadius: 'var(--r-2)', transition: 'background .12s, color .12s',
  };

  // ── Settings sub-tab rows — identical dot+rail pattern as nav sub-items ──────
  const renderSettingsSubs = (inFlyout = false) => (
    <div style={{ display: 'flex', flexDirection: 'column', margin: '1px 0 3px', position: 'relative' }}>
      {/* Tree rail */}
      <div style={{
        position: 'absolute', left: 23, top: -1, bottom: 15,
        width: 1, background: 'var(--hair-strong)', borderRadius: 1,
      }}/>
      {SETTINGS_SUBS.map(({ key, label, Icon }) => {
        const isSubActive = isSettingsActive && sub === key;
        return (
          <button key={key}
            onClick={() => { handleSettingsSub(key); if (inFlyout) { setMenuOpen(false); setSettingsOpen(false); } }}
            style={{
              ...itemBase,
              gap: 10, padding: '6px 12px 6px 34px',
              color: isSubActive ? 'var(--ink)' : 'var(--ink-2)',
              fontWeight: isSubActive ? 600 : 400,
              fontSize: 12.5, background: 'transparent', position: 'relative',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-2)'; e.currentTarget.style.color = 'var(--ink)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = isSubActive ? 'var(--ink)' : 'var(--ink-2)'; }}
          >
            {/* Active dot — matches nav sub-items exactly */}
            <span style={{
              position: 'absolute',
              left: isSubActive ? 20 : 21,
              top: '50%', transform: 'translateY(-50%)',
              width:  isSubActive ? 7 : 5,
              height: isSubActive ? 7 : 5,
              borderRadius: '50%',
              background: isSubActive ? 'var(--amber)' : 'var(--ink-4)',
              boxShadow: isSubActive ? '0 0 0 3px var(--surface)' : 'none',
              transition: 'all .15s',
            }}/>
            <span style={{ width: 16, height: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: isSubActive ? 'var(--amber-ink)' : 'var(--ink-3)' }}>
              <Icon size={14} strokeWidth={1.75}/>
            </span>
            {label}
          </button>
        );
      })}
    </div>
  );

  // ── Collapsed sidebar: flyout popup to the right ─────────────────────────────
  if (collapsed) {
    return (
      <div ref={flyoutRef} style={{ position: 'relative', borderTop: '0.5px solid var(--hair)', padding: '11px 0' }}>
        <button
          onClick={handleAvatarClick}
          title={`${user.name} · ${user.role}`}
          style={{
            ...itemBase, justifyContent: 'center',
            width: '100%', minHeight: 26, padding: 0,
            background: menuOpen ? 'var(--surface-2)' : 'transparent',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-2)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = menuOpen ? 'var(--surface-2)' : 'transparent'; }}
        >
          {avatar}
        </button>

        {menuOpen && (
          <div style={{
            position: 'fixed',
            left: (() => { const r = flyoutRef.current?.getBoundingClientRect(); return r ? r.right + 4 : 68; })(),
            bottom: (() => { const r = flyoutRef.current?.getBoundingClientRect(); return r ? window.innerHeight - r.bottom : 48; })(),
            zIndex: 300,
            background: 'var(--surface)', border: '0.5px solid var(--hair)',
            borderRadius: 'var(--r-3)', boxShadow: 'var(--shadow-panel)',
            minWidth: 210, overflow: 'hidden',
          }}>
            <div style={{ padding: '12px 14px 10px' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>{user.name}</div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{user.email}</div>
            </div>
            <div style={{ padding: '2px 6px 6px' }}>
              {/* Settings */}
              <button
                style={{ ...itemBase, gap: 10, padding: '7px 12px', fontSize: 12.5,
                  color: isSettingsActive ? 'var(--amber-ink)' : 'var(--ink-2)',
                  background: isSettingsActive ? 'var(--amber-bg)' : 'transparent' }}
                onClick={() => setSettingsOpen(o => !o)}
                onMouseEnter={e => { if (!isSettingsActive) e.currentTarget.style.background = 'var(--surface-2)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = isSettingsActive ? 'var(--amber-bg)' : 'transparent'; }}
              >
                <SettingsIcon size={15} strokeWidth={1.75} style={{ flexShrink: 0 }}/>
                <span style={{ flex: 1 }}>Settings</span>
                <Caret open={settingsOpen}/>
              </button>
              {settingsOpen && renderSettingsSubs(true)}
              {/* Sign out */}
              <button
                style={{ ...itemBase, gap: 10, padding: '7px 12px', fontSize: 12.5, color: 'var(--ink-2)', background: 'transparent' }}
                onClick={handleLogout}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-2)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <LogOut size={15} strokeWidth={1.75} style={{ flexShrink: 0 }}/> Sign out
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Expanded sidebar: inline, expands upward from the avatar ─────────────────
  return (
    <div>

      {/* Menu items — appear above the avatar row when open */}
      {menuOpen && (
        <div style={{ padding: '6px 6px 0' }}>

          {/* Settings row — highlighted amber when on settings tab */}
          <button
            style={{
              ...itemBase, gap: 11, padding: '9px 12px', fontSize: 13,
              color: isSettingsActive ? 'var(--amber-ink)' : 'var(--ink-2)',
              fontWeight: isSettingsActive ? 600 : 500,
              background: isSettingsActive ? 'var(--amber-bg)' : 'transparent',
            }}
            onClick={() => setSettingsOpen(o => !o)}
            onMouseEnter={e => { if (!isSettingsActive) e.currentTarget.style.background = 'var(--surface-2)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = isSettingsActive ? 'var(--amber-bg)' : 'transparent'; }}
          >
            <span style={{ flexShrink: 0, color: isSettingsActive ? 'var(--amber-ink)' : 'inherit' }}>
              <SettingsIcon size={18} strokeWidth={1.75}/>
            </span>
            <span style={{ flex: 1 }}>Settings</span>
            <span style={{ color: isSettingsActive ? 'var(--amber-ink)' : 'var(--ink-4)', flexShrink: 0 }}>
              <Caret open={settingsOpen}/>
            </span>
          </button>

          {/* Settings sub-tabs with tree rail + dots */}
          {settingsOpen && renderSettingsSubs(false)}

          {/* Sign out */}
          <button
            style={{ ...itemBase, gap: 11, padding: '9px 12px', fontSize: 13, color: 'var(--ink-2)', fontWeight: 500, background: 'transparent' }}
            onClick={handleLogout}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-2)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <LogOut size={18} strokeWidth={1.75} style={{ flexShrink: 0 }}/> Sign out
          </button>

          <div style={{ height: 4 }}/>
        </div>
      )}

      {/* When open: show full name + email above avatar */}
      {menuOpen && (
        <div style={{ padding: '8px 14px 2px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.4,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.name}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.email}
          </div>
        </div>
      )}

      {/* Avatar row — always visible: (M) FirstName */}
      <button
        onClick={handleAvatarClick}
        title={menuOpen ? 'Collapse' : `${user.name} · account & settings`}
        style={{
          ...itemBase, gap: 10,
          padding: '8px 14px 10px',
          background: 'transparent', fontSize: 12.5, color: 'var(--ink-2)', fontWeight: 500,
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-2)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
      >
        {avatar}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {firstName}
        </span>
      </button>
    </div>
  );
}

// ── Main Sidebar ───────────────────────────────────────────────────────────────
export default function Sidebar({
  tab, sub, navigate,
  canAccessTab,
  collapsed, onToggleCollapse,
  mobileOpen, onCloseMobile,
}) {
  const { isAdmin, can } = useUser();

  // Which spec section key maps to the current app tab
  const activeSpecKey = NAV_TREE.find(s => s.appTab === tab)?.key ?? null;

  // Open sections: initialise with the active section open
  const [openSections, setOpenSections] = useState(() => {
    const init = new Set();
    const active = NAV_TREE.find(s => s.appTab === tab);
    if (active?.subs) init.add(active.key);
    return init;
  });

  // Keep active section open when tab changes externally
  useEffect(() => {
    const active = NAV_TREE.find(s => s.appTab === tab);
    if (active?.subs) {
      setOpenSections(prev => {
        if (prev.has(active.key)) return prev;
        const next = new Set(prev);
        next.add(active.key);
        return next;
      });
    }
  }, [tab]);

  const toggleSection = (key) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // Check if a sub-tab is accessible
  const canAccessSub = (permKey) => isAdmin || can(permKey);

  // ── Collapsed item (icon only, 40px height) ───────────────────────────────
  const renderCollapsedSection = (section) => {
    const isActive = section.key === activeSpecKey;
    const isOpen   = openSections.has(section.key);
    const hasSubs  = !!section.subs;
    const accessible = canAccessTab ? canAccessTab(section.appTab) : true;

    const parentStyle = {
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: 40, borderRadius: 'var(--r-2)',
      color: isActive ? 'var(--amber-ink)' : accessible ? 'var(--ink-3)' : 'var(--ink-4)',
      background: isActive ? 'var(--amber-bg)' : 'transparent',
      boxShadow: isActive ? 'inset 0 0 0 1px var(--amber-hair)' : 'none',
      cursor: accessible ? 'pointer' : 'default',
      opacity: accessible ? 1 : 0.38,
      border: 'none', width: '100%', position: 'relative',
      transition: 'background .12s, color .12s',
    };

    return (
      <div key={section.key} style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {/* Parent icon */}
        <button
          title={section.label}
          style={parentStyle}
          onClick={() => {
            if (!accessible) return;
            if (hasSubs) toggleSection(section.key);
            else { navigate(section.appTab); }
          }}
          onMouseEnter={e => { if (!isActive && accessible) e.currentTarget.style.background = 'var(--surface-2)'; e.currentTarget.style.color = isActive ? 'var(--amber-ink)' : 'var(--ink-2)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = isActive ? 'var(--amber-bg)' : 'transparent'; e.currentTarget.style.color = isActive ? 'var(--amber-ink)' : accessible ? 'var(--ink-3)' : 'var(--ink-4)'; }}
        >
          <NavIcon name={section.icon} size={18}/>
        </button>

        {/* Sub-items (collapsed: icons only with dot indicator) */}
        {hasSubs && isOpen && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, margin: '1px 0 4px', position: 'relative' }}>
            {/* Tree rail */}
            <div style={{
              position: 'absolute', left: 11, top: -2, bottom: 15,
              width: 1, background: 'var(--hair-strong)', borderRadius: 1,
            }}/>
            {section.subs.filter(s => canAccessSub(s.permKey)).map(s => {
              const isSubActive = isActive && sub === s.appSub;
              return (
                <button key={s.appSub}
                  title={s.label}
                  onClick={() => navigate(section.appTab, s.appSub)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    height: 30, borderRadius: 'var(--r-1)', paddingLeft: 14,
                    color: isSubActive ? 'var(--amber-ink)' : 'var(--ink-3)',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    position: 'relative', width: '100%',
                    transition: 'background .12s, color .12s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-2)'; e.currentTarget.style.color = isSubActive ? 'var(--amber-ink)' : 'var(--ink-2)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = isSubActive ? 'var(--amber-ink)' : 'var(--ink-3)'; }}
                >
                  {/* Active dot */}
                  <span style={{
                    position: 'absolute',
                    left: isSubActive ? 8 : 9,
                    top: '50%', transform: 'translateY(-50%)',
                    width:  isSubActive ? 7 : 5,
                    height: isSubActive ? 7 : 5,
                    borderRadius: '50%',
                    background: isSubActive ? 'var(--amber)' : 'var(--ink-4)',
                    boxShadow: isSubActive ? '0 0 0 3px var(--surface)' : 'none',
                    transition: 'all .15s',
                  }}/>
                  <span style={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <NavIcon name={s.icon} size={15}/>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── Expanded item (icon + label + caret, 40px) ────────────────────────────
  const renderExpandedSection = (section) => {
    const isActive = section.key === activeSpecKey;
    const isOpen   = openSections.has(section.key);
    const hasSubs  = !!section.subs;
    const accessible = canAccessTab ? canAccessTab(section.appTab) : true;

    const parentStyle = {
      display: 'flex', alignItems: 'center', gap: 11,
      padding: '9px 12px', borderRadius: 'var(--r-2)',
      color: isActive ? 'var(--amber-ink)' : accessible ? 'var(--ink-2)' : 'var(--ink-3)',
      background: isActive ? 'var(--amber-bg)' : 'transparent',
      cursor: accessible ? 'pointer' : 'default',
      opacity: accessible ? 1 : 0.38,
      border: 'none', width: '100%', position: 'relative',
      transition: 'background .12s, color .12s',
    };

    return (
      <div key={section.key} style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <button
          style={parentStyle}
          onClick={() => {
            if (!accessible) return;
            if (hasSubs) toggleSection(section.key);
            else navigate(section.appTab);
          }}
          onMouseEnter={e => { if (!isActive && accessible) { e.currentTarget.style.background = 'var(--surface-2)'; } }}
          onMouseLeave={e => { e.currentTarget.style.background = isActive ? 'var(--amber-bg)' : 'transparent'; }}
        >
          <span style={{ flexShrink: 0, color: isActive ? 'var(--amber-ink)' : 'inherit' }}>
            <NavIcon name={section.icon} size={18}/>
          </span>
          <span style={{ flex: 1, textAlign: 'left', fontSize: 13, fontWeight: isActive ? 600 : 500, fontFamily: 'var(--font-ui)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {section.label}
          </span>
          {hasSubs && (
            <span style={{ color: isActive ? 'var(--amber-ink)' : 'var(--ink-4)', flexShrink: 0 }}>
              <Caret open={isOpen}/>
            </span>
          )}
        </button>

        {/* Sub-items (expanded: icon + label) */}
        {hasSubs && isOpen && (
          <div style={{ display: 'flex', flexDirection: 'column', margin: '1px 0 5px', position: 'relative' }}>
            {/* Tree rail */}
            <div style={{
              position: 'absolute', left: 23, top: -1, bottom: 15,
              width: 1, background: 'var(--hair-strong)', borderRadius: 1,
            }}/>
            {section.subs.filter(s => canAccessSub(s.permKey)).map(s => {
              const isSubActive = isActive && sub === s.appSub;
              return (
                <button key={s.appSub}
                  onClick={() => navigate(section.appTab, s.appSub)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '6px 12px 6px 34px',
                    color: isSubActive ? 'var(--ink)' : 'var(--ink-2)',
                    fontWeight: isSubActive ? 600 : 400,
                    fontSize: 12.5, fontFamily: 'var(--font-ui)',
                    borderRadius: 'var(--r-1)', background: 'transparent',
                    border: 'none', cursor: 'pointer',
                    position: 'relative', width: '100%',
                    transition: 'background .12s, color .12s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-2)'; e.currentTarget.style.color = 'var(--ink)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = isSubActive ? 'var(--ink)' : 'var(--ink-2)'; }}
                >
                  {/* Active dot */}
                  <span style={{
                    position: 'absolute',
                    left: isSubActive ? 20 : 21,
                    top: '50%', transform: 'translateY(-50%)',
                    width:  isSubActive ? 7 : 5,
                    height: isSubActive ? 7 : 5,
                    borderRadius: '50%',
                    background: isSubActive ? 'var(--amber)' : 'var(--ink-4)',
                    boxShadow: isSubActive ? '0 0 0 3px var(--surface)' : 'none',
                    transition: 'all .15s',
                  }}/>
                  {/* Sub-tab icon */}
                  <span style={{ width: 16, height: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: isSubActive ? 'var(--amber-ink)' : 'var(--ink-3)' }}>
                    <NavIcon name={s.icon} size={15}/>
                  </span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                    {s.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const isCollapsed = collapsed && !mobileOpen;

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div onClick={onCloseMobile} style={{
          position: 'fixed', inset: 0, top: 44,
          background: 'rgba(33,29,24,.4)', zIndex: 49,
        }}/>
      )}

      <aside className={['app-sidebar', collapsed ? 'sb-collapsed' : '', mobileOpen ? 'sb-mobile-open' : ''].filter(Boolean).join(' ')}>
        {/* Nav items */}
        <nav style={{
          flex: 1, padding: '7px 6px',
          display: 'flex', flexDirection: 'column', gap: 3,
          overflowY: 'auto',
          scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
        }}>
          {NAV_TREE.map(section =>
            isCollapsed
              ? renderCollapsedSection(section)
              : renderExpandedSection(section)
          )}
        </nav>

        {/* User menu */}
        <SidebarUserMenu
          collapsed={isCollapsed}
          onCloseMobile={onCloseMobile}
          navigate={navigate}
          tab={tab}
          sub={sub}
        />

        {/* Mobile close button */}
        {mobileOpen && (
          <div style={{ borderTop: '0.5px solid var(--hair)', padding: '4px 0' }}>
            <button onClick={onCloseMobile} title="Close" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '100%', padding: '7px 0',
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--ink-3)',
            }}>
              <X size={14} strokeWidth={1.75}/>
            </button>
          </div>
        )}
      </aside>

      {/* Collapse tab — desktop only */}
      {!mobileOpen && (
        <button
          className="sb-collapse-btn"
          onClick={onToggleCollapse}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          style={{
            position: 'fixed',
            left: collapsed ? 48 : 192,
            top: 64, zIndex: 21,
            width: 14, height: 36,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--surface)',
            border: '0.5px solid var(--hair)', borderLeft: 'none',
            borderRadius: '0 var(--r-2) var(--r-2) 0',
            cursor: 'pointer', color: 'var(--ink-3)', padding: 0,
            transition: 'left 0.18s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--ink)'; e.currentTarget.style.background = 'var(--surface-2)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-3)'; e.currentTarget.style.background = 'var(--surface)'; }}
        >
          {collapsed ? <ChevronRight size={10} strokeWidth={2.5}/> : <ChevronLeft size={10} strokeWidth={2.5}/>}
        </button>
      )}
    </>
  );
}
